import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format, addDays, differenceInDays } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import { Plus, CalendarDays, MapPin, Clock, Edit, Search, Check, X as XIcon, AlertCircle, Trash2, CheckCircle, ChevronDown, FileDown, FileText, Printer, Bookmark } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-amber-500/10 text-amber-600 dark:text-amber-400", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", icon: Check },
  rejected: { label: "Rejected", color: "bg-red-500/10 text-red-600 dark:text-red-400", icon: XIcon },
  complete: { label: "Complete", color: "bg-blue-500/10 text-blue-600 dark:text-blue-400", icon: CheckCircle },
};

export default function EventsPage() {
  const { authAxios, isAdmin, isSubAdmin, isTeamLead, isEventHost, canEdit, canApprove } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(searchParams.get("new") === "true");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState({ title: "", description: "", location: "", start_date: null, end_date: null, contact_name: "", contact_phone: "", budget_limit: "" });
  const [submitting, setSubmitting] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const canManageStatus = isAdmin || isSubAdmin;

  const fetchEvents = useCallback(async () => {
    try {
      const api = authAxios();
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const res = await api.get("/events", { params });
      setEvents(res.data);
    } catch (err) {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [authAxios, statusFilter]);

  const fetchTemplates = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get("/templates");
      setTemplates(res.data);
    } catch {}
  }, [authAxios]);

  useEffect(() => { fetchEvents(); fetchTemplates(); }, [fetchEvents, fetchTemplates]);

  const generateDays = (start, end) => {
    if (!start || !end) return [];
    const days = [];
    const numDays = differenceInDays(end, start) + 1;
    for (let i = 0; i < Math.min(numDays, 7); i++) {
      const d = addDays(start, i);
      days.push({
        day_number: i + 1,
        date: format(d, "yyyy-MM-dd"),
        day_name: DAYS_OF_WEEK[d.getDay()],
      });
    }
    return days;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) {
      toast.error("Please select start and end dates");
      return;
    }
    const days = generateDays(form.start_date, form.end_date);
    if (days.length > 7) {
      toast.error("Events can be 1-7 days maximum");
      return;
    }
    setSubmitting(true);
    try {
      const api = authAxios();
      const payload = {
        title: form.title,
        description: form.description,
        location: form.location,
        start_date: format(form.start_date, "yyyy-MM-dd"),
        end_date: format(form.end_date, "yyyy-MM-dd"),
        days,
        contact_name: form.contact_name,
        contact_phone: form.contact_phone,
        budget_limit: form.budget_limit ? parseFloat(form.budget_limit) : null,
      };
      const res = await api.post("/events", payload);
      toast.success("Event created!");
      setDialogOpen(false);
      setForm({ title: "", description: "", location: "", start_date: null, end_date: null, contact_name: "", contact_phone: "", budget_limit: "" });
      navigate(`/events/${res.data.event_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create event");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSingle = async (eventId) => {
    try {
      const api = authAxios();
      await api.delete(`/events/${eventId}`);
      toast.success("Event deleted");
      setDeleteConfirm(null);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete event");
    }
  };

  const handleBulkDelete = async () => {
    try {
      const api = authAxios();
      const ids = Array.from(selectedEvents);
      await api.post("/events/bulk-delete", { event_ids: ids });
      toast.success(`${ids.length} event(s) deleted`);
      setSelectedEvents(new Set());
      setDeleteConfirm(null);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete events");
    }
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      const api = authAxios();
      await api.put(`/events/${eventId}/status`, { status: newStatus });
      toast.success(`Event status updated to ${newStatus}`);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const toggleSelect = (eventId) => {
    setSelectedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const selectAllRejected = () => {
    const rejectedIds = filtered.filter((e) => e.status === "rejected").map((e) => e.event_id);
    setSelectedEvents(new Set(rejectedIds));
  };

  const clearSelection = () => setSelectedEvents(new Set());

  const filtered = events.filter((e) =>
    e.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const rejectedCount = filtered.filter((e) => e.status === "rejected").length;

  // ── Export Functions ──
  const exportEventsCSV = async () => {
    try {
      const api = authAxios();
      const res = await api.get("/export/events/csv", { responseType: "blob" });
      saveAs(res.data, "events_summary.csv");
      toast.success("CSV downloaded!");
    } catch {
      toast.error("Failed to export CSV");
    }
  };

  const exportEventsPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Events Summary", 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()} | Total: ${filtered.length} events`, 14, 28);

    const tableData = filtered.map((e) => [
      e.title, e.status?.toUpperCase(), e.start_date, e.end_date,
      e.location || "-", `${e.days?.length || 0} day(s)`, e.managed_by_name || "-"
    ]);

    autoTable(doc, {
      startY: 34,
      head: [["Title", "Status", "Start", "End", "Location", "Duration", "Manager"]],
      body: tableData,
      theme: "grid",
      headStyles: { fillColor: [70, 70, 200], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 40 } },
    });

    const pdfBlob = doc.output("blob");
    saveAs(pdfBlob, "events_summary.pdf");
    toast.success("PDF downloaded!");
  };

  const handlePrintEvents = () => {
    window.print();
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 w-full space-y-8" data-testid="events-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground">Events</h1>
          <p className="text-muted-foreground mt-1">Manage your community center meetings</p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={exportEventsCSV} data-testid="events-export-csv-btn">
            <FileDown className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportEventsPDF} data-testid="events-export-pdf-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrintEvents} data-testid="events-print-btn">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <div className="flex gap-2">
            {templates.length > 0 && (
              <Button variant="outline" className="shadow-sm" onClick={() => setTemplateDialogOpen(true)} data-testid="from-template-btn">
                <Bookmark className="h-4 w-4 mr-2" /> From Template
              </Button>
            )}
            <DialogTrigger asChild>
              <Button className="shadow-md" data-testid="new-event-btn">
                <Plus className="h-4 w-4 mr-2" /> New Event
              </Button>
            </DialogTrigger>
          </div>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-event-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Create New Event</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Event Title</Label>
                <Input
                  placeholder="Annual Community Meeting"
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required data-testid="event-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Describe the event..."
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3} data-testid="event-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  placeholder="Community Center, Main Hall"
                  value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  data-testid="event-location-input"
                />
              </div>
              {isEventHost && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Person Name</Label>
                    <Input
                      placeholder="Your name"
                      value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                      data-testid="event-contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Phone</Label>
                    <Input
                      placeholder="(555) 123-4567"
                      value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                      data-testid="event-contact-phone-input"
                    />
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Budget Limit ($)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  placeholder="Optional total budget for this event"
                  value={form.budget_limit} onChange={(e) => setForm({ ...form, budget_limit: e.target.value })}
                  data-testid="event-budget-limit-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="event-start-date-btn">
                        <CalendarDays className="mr-2 h-4 w-4" />
                        {form.start_date ? format(form.start_date, "MMM d, yyyy") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single" selected={form.start_date}
                        onSelect={(d) => setForm({ ...form, start_date: d, end_date: form.end_date && d > form.end_date ? d : form.end_date })}
                        data-testid="start-date-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="event-end-date-btn">
                        <CalendarDays className="mr-2 h-4 w-4" />
                        {form.end_date ? format(form.end_date, "MMM d, yyyy") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single" selected={form.end_date}
                        onSelect={(d) => setForm({ ...form, end_date: d })}
                        disabled={(d) => form.start_date && (d < form.start_date || differenceInDays(d, form.start_date) >= 7)}
                        data-testid="end-date-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              {form.start_date && form.end_date && (
                <div className="p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground" data-testid="event-days-preview">
                  <p className="font-medium text-foreground mb-1">Event Duration: {differenceInDays(form.end_date, form.start_date) + 1} day(s)</p>
                  {generateDays(form.start_date, form.end_date).map((d) => (
                    <span key={d.day_number} className="inline-block mr-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded-md">
                      Day {d.day_number}: {d.day_name}
                    </span>
                  ))}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={submitting} data-testid="create-event-submit-btn">
                {submitting ? "Creating..." : "Create Event"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search events..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10" data-testid="event-search-input"
          />
        </div>
        {(isAdmin || isSubAdmin || isTeamLead) && (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="event-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
        )}
        {isEventHost && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded-lg">
            <AlertCircle className="h-4 w-4" />
            Events require admin approval before appearing on the portal
          </div>
        )}
      </div>

      {/* Bulk Actions Bar - Admin/Sub Admin */}
      {canManageStatus && filtered.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50" data-testid="bulk-actions-bar">
          {selectedEvents.size > 0 ? (
            <>
              <span className="text-sm font-medium text-foreground" data-testid="selected-count">
                {selectedEvents.size} selected
              </span>
              <Button
                variant="destructive" size="sm"
                onClick={() => setDeleteConfirm({ type: "bulk", count: selectedEvents.size })}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete Selected
              </Button>
              <Button variant="outline" size="sm" onClick={clearSelection} data-testid="clear-selection-btn">
                Clear
              </Button>
            </>
          ) : (
            <>
              <span className="text-sm text-muted-foreground">Bulk Actions:</span>
              {rejectedCount > 0 && (
                <Button variant="outline" size="sm" onClick={selectAllRejected} data-testid="select-all-rejected-btn">
                  <XIcon className="h-3.5 w-3.5 mr-1.5 text-red-500" />
                  Select All Rejected ({rejectedCount})
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {/* Events Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <div key={i} className="h-48 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
          {filtered.map((event) => {
            const isSelected = selectedEvents.has(event.event_id);
            return (
              <Card
                key={event.event_id}
                className={`border-border/50 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer group relative ${isSelected ? "ring-2 ring-primary border-primary" : ""}`}
                data-testid={`event-card-${event.event_id}`}
              >
                {/* Checkbox for selection - Admin/Sub Admin */}
                {canManageStatus && (
                  <div
                    className="absolute top-3 left-3 z-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelect(event.event_id)}
                      data-testid={`select-event-${event.event_id}`}
                    />
                  </div>
                )}
                <div onClick={() => navigate(`/events/${event.event_id}`)}>
                  <CardHeader className={`pb-3 ${canManageStatus ? "pl-10" : ""}`}>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1 min-w-0 flex-1">
                        <CardTitle className="font-heading text-lg group-hover:text-primary transition-colors truncate">{event.title}</CardTitle>
                        {event.status && (
                          <Badge className={`text-xs ${STATUS_CONFIG[event.status]?.color || "bg-muted text-muted-foreground"}`} data-testid={`event-status-badge-${event.event_id}`}>
                            {STATUS_CONFIG[event.status]?.label || event.status}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                        {canEdit && (
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => navigate(`/events/${event.event_id}`)}
                            data-testid={`edit-event-${event.event_id}`}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canManageStatus && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setDeleteConfirm({ type: "single", eventId: event.event_id, title: event.title })}
                            data-testid={`delete-event-${event.event_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {event.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>
                    )}
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {event.start_date} - {event.end_date}
                      </span>
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {event.location}
                        </span>
                      )}
                    </div>
                    {event.days?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {event.days.map((d) => (
                          <span key={d.day_number} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-md font-medium">
                            {d.day_name?.slice(0, 3)}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                      {event.managed_by_name && (
                        <Badge variant="secondary" className="text-[10px]" data-testid={`managed-by-${event.event_id}`}>
                          Managed by: {event.managed_by_name}
                        </Badge>
                      )}
                      {event.contact_name && (
                        <Badge variant="outline" className="text-[10px]" data-testid={`contact-${event.event_id}`}>
                          Contact: {event.contact_name} {event.contact_phone ? `| ${event.contact_phone}` : ""}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </div>
                {/* Status Change - Admin/Sub Admin */}
                {canManageStatus && (
                  <div className="px-6 pb-4" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full text-xs" data-testid={`change-status-${event.event_id}`}>
                          Change Status
                          <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        {Object.entries(STATUS_CONFIG).filter(([key]) => key !== event.status).map(([key, cfg]) => {
                          const Icon = cfg.icon;
                          return (
                            <DropdownMenuItem
                              key={key}
                              onClick={() => handleStatusChange(event.event_id, key)}
                              data-testid={`status-option-${key}-${event.event_id}`}
                            >
                              <Icon className="h-3.5 w-3.5 mr-2" />
                              {cfg.label}
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16" data-testid="no-events">
          <CalendarDays className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
          <h3 className="text-lg font-heading font-semibold text-foreground">No events yet</h3>
          <p className="text-muted-foreground mt-1">Create your first event to get started</p>
          <Button className="mt-4" onClick={() => setDialogOpen(true)} data-testid="create-first-event-btn">
            <Plus className="h-4 w-4 mr-2" /> Create Event
          </Button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent data-testid="delete-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              {deleteConfirm?.type === "bulk" ? "Delete Selected Events" : "Delete Event"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            {deleteConfirm?.type === "bulk"
              ? `Are you sure you want to delete ${deleteConfirm.count} selected event(s)? This will also remove all associated meals, notes, and chat messages.`
              : <>Are you sure you want to delete <strong>{deleteConfirm?.title}</strong>? This will also remove all associated meals, notes, and chat messages.</>}
          </p>
          <p className="text-sm text-destructive font-medium">This action cannot be undone.</p>
          <div className="flex gap-3 justify-end mt-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirm?.type === "bulk" ? handleBulkDelete() : handleDeleteSingle(deleteConfirm?.eventId)}
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {deleteConfirm?.type === "bulk" ? `Delete ${deleteConfirm.count} Event(s)` : "Delete Event"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Create from Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {templates.map((t) => (
              <Card key={t.template_id} className="cursor-pointer hover:shadow-md transition-shadow border-border/50" data-testid={`template-item-${t.template_id}`}>
                <CardContent className="p-4" onClick={async () => {
                  try {
                    const api = authAxios();
                    const res = await api.post(`/templates/${t.template_id}/use`, {
                      title: t.event_snapshot?.title || t.name,
                      start_date: "", end_date: "", days: t.event_snapshot?.days || [],
                    });
                    toast.success("Event created from template!");
                    setTemplateDialogOpen(false);
                    navigate(`/events/${res.data.event_id}`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to create from template");
                  }
                }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-foreground">{t.name}</h3>
                      {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                      <p className="text-xs text-muted-foreground mt-1">
                        {t.event_snapshot?.location && `${t.event_snapshot.location} · `}
                        {t.meals_snapshot?.length || 0} meals · by {t.creator_name}
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-xs">{t.event_snapshot?.days?.length || 0} days</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
            {templates.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No templates saved yet. Save an event as a template from the event detail page.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
